<template>
  <div class="import-region-ryg-container">
    <div class="card bg-white" style="border-radius: 1rem">
      <div class="card-body">
        <div class="mb-3">
          <h4 class="mb-1 d-flex align-items-center gap-2">
            <i class="fas fa-flask text-success"></i>
            <span>Migración region_ryg (prestadores)</span>
          </h4>
          <p class="text-muted mb-0">
            Sube un Excel (fila 1 = cabeceras) con columna
            <strong>cedula</strong> como
            <strong>texto</strong> en Excel (formato celda Texto; no número).
            Se recorre la colección
            <strong>serviceProviders</strong> y se compara
            <code>providerDni</code> con cada cédula del archivo. Si hay
            match → <code>region_ryg: "Caps"</code>; si no →
            <code>region_ryg: ""</code>. También actualiza
            <code>user.provider.region_ryg</code> cuando el usuario está
            vinculado como prestador.
          </p>
        </div>

        <hr class="my-3" />

        <div class="row g-3 align-items-end">
          <div class="col-12 col-md-6">
            <label class="form-label">Archivo Excel</label>
            <input
              ref="fileInput"
              class="form-control"
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              @change="onFileChange"
            />
            <small v-if="fileName" class="text-muted d-block mt-1">
              {{ fileName }}
            </small>
          </div>

          <div
            class="col-12 col-md-6 d-flex gap-2 flex-wrap justify-content-md-end"
          >
            <button
              class="btn btn-outline-success btn-sm"
              :disabled="previewLoading || !excelBase64"
              @click="preview"
            >
              <span
                v-if="previewLoading"
                class="spinner-border spinner-border-sm me-1"
                role="status"
              ></span>
              {{ previewLoading ? "Analizando..." : "Vista previa" }}
            </button>
            <button
              class="btn btn-success btn-sm"
              :disabled="applyLoading || !canApply"
              @click="apply"
            >
              <span
                v-if="applyLoading"
                class="spinner-border spinner-border-sm me-1"
                role="status"
              ></span>
              {{
                applyLoading
                  ? "Aplicando (puede tardar 1–3 min)..."
                  : "Aplicar migración"
              }}
            </button>
          </div>
        </div>

        <div
          v-if="error"
          class="alert alert-danger mt-3 mb-0 py-2"
          role="alert"
        >
          {{ error }}
        </div>

        <div
          v-if="numericExcelRowsCount > 0"
          class="alert alert-warning mt-3 mb-0 py-2 small"
          role="alert"
        >
          <strong>{{ numericExcelRowsCount }} fila(s)</strong> del Excel tienen
          la cédula guardada como <strong>número</strong> y se omitieron (puede
          perderse el 0 inicial). Formatea la columna <code>cedula</code> como
          <strong>Texto</strong> y vuelve a subir el archivo.
          <span v-if="numericExcelRowsSample.length">
            Filas: {{ numericExcelRowsSample.join(", ") }}
            <span v-if="numericExcelRowsCount > numericExcelRowsSample.length"
              >…</span
            >
          </span>
        </div>

        <div
          v-if="summary"
          class="alert alert-light border mt-3 mb-0 py-2 small"
          role="region"
        >
          <strong>Resumen:</strong>
          {{ summary.totalProviders }} prestadores en Firestore —
          {{ summary.excelCedulas }} cédulas válidas (texto) en Excel —
          <span class="text-success">{{ summary.matchCaps }} con Caps</span>,
          {{ summary.noMatchEmpty }} con vacío.
        </div>

        <div
          v-if="applyResult"
          class="alert alert-success mt-3 mb-0 py-2"
          role="alert"
        >
          {{ applyResult.message || "Migración completada" }}
        </div>

        <div v-if="previewRows.length" class="mt-3">
          <div class="row g-2 align-items-end mb-2">
            <div class="col-12 col-md-4">
              <label class="form-label small mb-1">Buscar en tabla</label>
              <input
                v-model.trim="dniFilter"
                type="text"
                class="form-control form-control-sm"
                placeholder="Cédula / DNI"
              />
            </div>
            <div class="col-12 col-md-8 small text-muted">
              <span v-if="rowsTruncated">
                Tabla: {{ previewRowsShown }} de
                {{ totalRows }} prestadores (prioridad: con match).
                <strong>Aplicar migración</strong> actualiza los
                {{ summary.totalProviders }}.
              </span>
              <span v-else>
                Mostrando los {{ previewRows.length }} prestadores.
              </span>
            </div>
          </div>
          <div class="table-responsive">
          <table class="table table-sm table-striped align-middle mb-0">
            <thead>
              <tr>
                <th>Cédula / DNI</th>
                <th>Nombre</th>
                <th>region_ryg actual</th>
                <th>region_ryg nuevo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredPreviewRows" :key="row.providerId">
                <td>{{ row.providerDni || "—" }}</td>
                <td>{{ row.providerName || "—" }}</td>
                <td>
                  <code v-if="row.regionRygActual != null">{{
                    row.regionRygActual || '""'
                  }}</code>
                  <span v-else class="text-muted">(sin campo)</span>
                </td>
                <td>
                  <code>{{ row.regionRygNuevo || '""' }}</code>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="
                      row.status === 'match'
                        ? 'bg-success'
                        : 'bg-secondary'
                    "
                  >
                    {{ row.status === "match" ? "Match → Caps" : "Sin match" }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
          <p
            v-if="dniFilter && !filteredPreviewRows.length"
            class="text-muted small mt-2 mb-0"
          >
            Sin resultados para «{{ dniFilter }}» en las filas mostradas. Si hay
            más de 500 prestadores, el match puede estar fuera de la tabla; revisa
            el resumen arriba (con Caps / vacío).
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ImportRegionRyg",
  props: {
    tokenAuth: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      role: localStorage.getItem("session"),
      excelBase64: "",
      fileName: "",
      previewLoading: false,
      applyLoading: false,
      error: "",
      summary: null,
      previewRows: [],
      rowsTruncated: false,
      applyResult: null,
      numericExcelRowsCount: 0,
      numericExcelRowsSample: [],
      dniFilter: "",
      totalRows: 0,
      previewRowsShown: 0,
    };
  },
  computed: {
    filteredPreviewRows() {
      const q = String(this.dniFilter || "").replace(/\D/g, "");
      if (!q) return this.previewRows;
      return this.previewRows.filter((row) => {
        const dni = String(row.providerDni || "").replace(/\D/g, "");
        return dni.includes(q);
      });
    },
    canApply() {
      return (
        !!this.excelBase64 &&
        !this.previewLoading &&
        !this.applyLoading &&
        this.summary &&
        this.summary.totalProviders >= 0
      );
    },
  },
  methods: {
    onFileChange(e) {
      this.error = "";
      this.summary = null;
      this.previewRows = [];
      this.applyResult = null;
      this.rowsTruncated = false;
      this.numericExcelRowsCount = 0;
      this.numericExcelRowsSample = [];
      this.dniFilter = "";
      this.totalRows = 0;
      this.previewRowsShown = 0;

      const file = e.target.files && e.target.files[0];
      if (!file) {
        this.excelBase64 = "";
        this.fileName = "";
        return;
      }
      this.fileName = file.name;
      const reader = new FileReader();
      reader.onload = () => {
        this.excelBase64 = String(reader.result || "");
        this.preview();
      };
      reader.onerror = () => {
        this.excelBase64 = "";
        this.error = "No se pudo leer el archivo";
      };
      reader.readAsDataURL(file);
    },
    async preview() {
      if (!this.excelBase64) {
        this.error = "Selecciona un Excel primero";
        return;
      }
      this.previewLoading = true;
      this.error = "";
      this.applyResult = null;
      try {
        const res = await this.$https.post(
          "/hubspot/regionRygExcelPreview",
          {
            tokenAuth: this.tokenAuth,
            role: this.role,
            excelBase64: this.excelBase64,
          },
          { timeout: 180000 },
        );
        if (res.data && res.data.code === 200) {
          this.summary = res.data.summary;
          this.previewRows = res.data.rows || [];
          this.rowsTruncated = !!res.data.rowsTruncated;
          this.totalRows = Number(res.data.totalRows) || 0;
          this.previewRowsShown =
            Number(res.data.previewRowsShown) || this.previewRows.length;
          this.numericExcelRowsCount =
            Number(res.data.numericExcelRowsCount) || 0;
          this.numericExcelRowsSample = res.data.numericExcelRows || [];
        } else {
          this.error = res.data?.error || "No se pudo analizar el Excel";
        }
      } catch (err) {
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al analizar el Excel";
      } finally {
        this.previewLoading = false;
      }
    },
    async apply() {
      if (!this.canApply) return;
      const ok = window.confirm(
        `¿Aplicar region_ryg en ${this.summary.totalProviders} prestador(es) según el Excel? Los que no coincidan quedarán con valor vacío.`,
      );
      if (!ok) return;

      this.applyLoading = true;
      this.error = "";
      try {
        const res = await this.$https.post(
          "/hubspot/regionRygExcelApply",
          {
            tokenAuth: this.tokenAuth,
            role: this.role,
            excelBase64: this.excelBase64,
          },
          { timeout: 600000 },
        );
        if (res.data && res.data.code === 200) {
          this.applyResult = res.data;
          this.summary = res.data.summary || this.summary;
          this.numericExcelRowsCount =
            Number(res.data.numericExcelRowsCount) || 0;
          this.numericExcelRowsSample = res.data.numericExcelRows || [];
          await this.preview();
        } else {
          this.error = res.data?.error || "No se pudo aplicar la migración";
        }
      } catch (err) {
        const isTimeout =
          err?.code === "ECONNABORTED" ||
          String(err?.message || "").includes("timeout");
        this.error = isTimeout
          ? "La migración tardó demasiado en el navegador. Reinicia functions, vuelve a intentar (es idempotente). Si falla de nuevo, revisa en Firestore si serviceProviders ya se actualizó."
          : err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Error al aplicar migración";
      } finally {
        this.applyLoading = false;
      }
    },
  },
};
</script>
